import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {IVideo, IVideoGenerationStatus} from "../../../../../sidebar/sidebar.component";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {ButtonModule} from "primeng/button";
import {SliderModule} from "primeng/slider";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {CardModule} from "primeng/card";
import {FormsModule} from "@angular/forms";
import {NgIf} from "@angular/common";
import {finalize} from "rxjs";
import {VideoCollectionService} from "../../../../../../../services/video-collection.service";
import {NewProject} from "../../../../../../../interfaces/app.interfaces";

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [
    ButtonModule,
    SliderModule,
    ProgressSpinnerModule,
    CardModule,
    FormsModule,
    NgIf
  ],
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss'
})
export class VideoPlayerComponent implements OnInit, OnChanges {

  @Input() video!: IVideo;
  @Input() videoCollection!: NewProject;

  @Output() isVideoLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() videoPreviewUrl: EventEmitter<string> = new EventEmitter<string>();
  @Output() videoBlob: EventEmitter<Blob> = new EventEmitter<Blob>();

  @ViewChild('videoPlayer') videoRef!: ElementRef<HTMLVideoElement>;

  videoUrl: SafeResourceUrl | null = null;
  isPlaying = false;
  isMuted = false;
  currentTime = 0;
  duration = 0;
  isLoading = false;
  animationFrameId: number = 0;

  constructor(
      private sanitizer: DomSanitizer,
      private videoService: VideoCollectionService,
      private zone: NgZone,
      private cdr: ChangeDetectorRef
  ) {}

  getVideoThumbnail(videoBlob: Blob, seekTo = 1): Promise<string> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = URL.createObjectURL(videoBlob);
      video.muted = true;
      video.playsInline = true;
      video.onloadedmetadata = () => {
        if (video.duration < seekTo) seekTo = 0;
        video.currentTime = seekTo;
      };
      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Cannot get canvas context');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (blob) {
            const thumbnailUrl = URL.createObjectURL(blob);
            resolve(thumbnailUrl);
          } else {
            reject('Failed to create thumbnail blob');
          }
        }, 'image/jpeg');
      };
      video.onerror = (e) => {
        reject('Failed to load video for thumbnail');
      };
    });
  }

  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const paddedMins = mins.toString().padStart(2, '0');
    const paddedSecs = secs.toString().padStart(2, '0');
    return `${paddedMins}:${paddedSecs}`;
  }

  loadVideo() {
    this.isVideoLoading.emit(true);
    this.videoService.getVideoContentById(this.video.id, this.videoCollection.id).pipe(
        finalize(() => {
          this.isVideoLoading.emit(false);
        })
    ).subscribe(video => {
      const objectUrl = URL.createObjectURL(video);
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
      this.getVideoThumbnail(video, 1).then(thumbnailUrl => {
        this.videoPreviewUrl.emit(thumbnailUrl);
      });
      this.videoBlob.emit(video);

      setTimeout(() => {
        const video = this.videoRef.nativeElement;
        video.addEventListener('loadedmetadata', () => {
          this.duration = video.duration;
        });
        video.addEventListener('ended', () => {
          this.isPlaying = false;
          this.stopTrackingCurrentTime();
          this.currentTime = 0;
          video.currentTime = 0;
        });
      }, 0);
    })
  }

  startTrackingCurrentTime() {
    const video = this.videoRef.nativeElement;
    const update = () => {
      this.currentTime = video.currentTime;
      this.animationFrameId = requestAnimationFrame(update);
    };
    this.animationFrameId = requestAnimationFrame(update);
  }

  stopTrackingCurrentTime() {
    cancelAnimationFrame(this.animationFrameId);
  }

  togglePlayPause() {
    const video = this.videoRef.nativeElement;
    if (video.paused) {
      video.play();
      this.isPlaying = true;
      this.startTrackingCurrentTime();
    } else {
      video.pause();
      this.isPlaying = false;
      this.stopTrackingCurrentTime();
    }
  }

  toggleMute() {
    if (this.videoRef) {
      const video = this.videoRef.nativeElement;
      video.muted = !video.muted;
      this.isMuted = video.muted;
    }
  }

  onSeek(event: any) {
    if (this.videoRef) {
      const video = this.videoRef.nativeElement;
      video.currentTime = event.value;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const video = changes['video'];
    if ((video && !video.firstChange)) {
      if (this.video.generationStatus === IVideoGenerationStatus.CompletedSuccessfully) {
        this.loadVideo();
      }
    }
  }

  ngOnInit(): void {
    if (this.video.generationStatus === IVideoGenerationStatus.CompletedSuccessfully) {
      this.loadVideo();
    }
  }

}

